import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCardsBlank, faPen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode, RefObject } from 'react';
import { useState } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { ButtonBaseWithConfirmDialog } from '@sorare/core/src/components/form/ButtonWithConfirmDialog';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useDecks } from '@sorare/core/src/hooks/decks/useDecks';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useUpdateSportProfile } from '@sorare/core/src/hooks/useUpdateSportProfile';
import type { blockchainRarities } from '@sorare/core/src/lib/cards';
import { playableBlockchainRarities } from '@sorare/core/src/lib/cards';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { ProfileCta } from '../ProfileCta';
import { ChooseDeck } from './ChooseDeck';
import { GalleryCards } from './GalleryCards';
import type {
  GalleryNohighlightedDeckQuery,
  GalleryNohighlightedDeckQueryVariables,
  Gallery_anyCard,
  Gallery_deck,
  Gallery_publicUserInfoInterface,
} from './__generated__/index.graphql';
import decksBg from './assets/decks-bg.jpg';

const Root = styled(Vertical).attrs({ gap: 3 })`
  border-radius: var(--double-unit);
  position: relative;
  overflow: hidden;
  background:
    no-repeat center / cover url(${decksBg}) padding-box,
    var(--c-nd-50);
  border: 2px solid var(--c-nd-100-opaque);
`;
const CardsWrapper = styled(Vertical)`
  width: 100%;
`;
const Header = styled(SBHorizontal)`
  width: 100%;
  padding: 0 var(--intermediate-unit);
`;
const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  z-index: 2;
  min-height: 330px;
  padding: var(--intermediate-unit) 0;
  flex: 1;
  justify-content: space-between;
`;
const HighlightedDeckTitle = styled(SBHorizontal)`
  padding: 0 var(--intermediate-unit);
`;
const Footer = styled(SBHorizontal)`
  padding: 0 var(--intermediate-unit);
  align-self: stretch;
  & > * {
    flex: 1;
  }
`;

const messages = defineMessages({
  cards: {
    id: 'ManagerProfile.Gallery.cards',
    defaultMessage: 'Cards',
  },
  seeCards: {
    id: 'ManagerProfile.Gallery.seeCards',
    defaultMessage: 'See cards',
  },
  seeMyCards: {
    id: 'ManagerProfile.Gallery.seeMyCards',
    defaultMessage: 'See my cards',
  },
  seeCollections: {
    id: 'ManagerProfile.Gallery.seeCollections',
    defaultMessage: 'See collections',
  },
});

type ScarcityCountProps = {
  galleryUrl: string;
  slug?: string;
  scarcity: (typeof blockchainRarities)[number] | 'common';
  count: number;
};
const ScarcityCount = ({
  galleryUrl,
  slug,
  scarcity,
  count,
}: ScarcityCountProps) => {
  const filterQuery: Partial<Record<SEARCH_PARAMS, unknown>> = {
    [SEARCH_PARAMS.RARITY]: scarcity,
  };

  if (scarcity === Rarity.custom_series) {
    filterQuery[SEARCH_PARAMS.NON_PLAYABLE_CARDS] = 'true';
  }
  return (
    <Link
      key={scarcity}
      to={`${generatePath(galleryUrl, {
        slug,
      })}?${buildFilterQuery(filterQuery)}`}
    >
      <Horizontal>
        <ScarcityIcon rarity={scarcity as Rarity} />
        <BodyS bold color="var(--c-nd-600-opaque)">
          <FormattedNumber value={count} notation="compact" />
        </BodyS>
      </Horizontal>
    </Link>
  );
};

const MAX_CARDS_TO_DISPLAY = 5;
const cardFragment = gql`
  fragment Gallery_anyCard on AnyCardInterface {
    slug
    rarityTyped
    pictureUrl
    ...GalleryCards_anyCard
  }
  ${GalleryCards.fragments.anyCard}
` as TypedDocumentNode<Gallery_anyCard>;
const GALLERY_NO_HIGHLIGHTED_DECK_QUERY = gql`
  query GalleryNohighlightedDeckQuery(
    $sport: Sport!
    $userSlug: String!
    $firstCardsToDisplay: Int
  ) {
    user(slug: $userSlug) {
      slug
      cards(
        sport: $sport
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        sorts: [RARITIES]
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
    }
  }
  ${cardFragment}
` as TypedDocumentNode<
  GalleryNohighlightedDeckQuery,
  GalleryNohighlightedDeckQueryVariables
>;

type Props = {
  sport: Sport;
  onboardingComponent?: ReactNode;
  shouldShowOnboarding?: boolean;
  highlightedDeck?: Gallery_deck | null;
  onAddDeck: () => void;
  onEditDeck: (slug: string) => void;
  extra?: ReactNode;
  scrollRef?: RefObject<HTMLDivElement | null>;
  user: Gallery_publicUserInfoInterface;
};
export const Gallery = ({
  sport,
  highlightedDeck,
  onAddDeck,
  onEditDeck,
  onboardingComponent,
  shouldShowOnboarding,
  extra,
  user,
  scrollRef,
}: Props) => {
  const hasHighlightedDeck =
    !!highlightedDeck && highlightedDeck?.cards?.nodes.length > 0;

  const { data } = useQuery(GALLERY_NO_HIGHLIGHTED_DECK_QUERY, {
    variables: {
      sport,
      userSlug: user.slug,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
    },
    skip: !!hasHighlightedDeck,
  });

  const { slug } = user;
  const { formatMessage } = useIntl();
  const { isMe } = useCurrentUserContext();
  const updateSportProfile = useUpdateSportProfile();

  const isMeUser = isMe(slug);
  const { decks, loading } = useDecks({ sport, skip: !isMeUser });
  const [open, setOpen] = useState(false);
  const { generateSportContextPath } = useSportContext();
  const { cardCounts } = user;

  const galleryUrl = isMeUser
    ? generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
        params: {
          slug,
        },
      });

  const cardsToDisplay =
    (hasHighlightedDeck
      ? highlightedDeck.cards.nodes
      : data?.user.cards.nodes) || [];

  const onEditDeckClick = () => {
    if (!loading && !decks.length) {
      onAddDeck();
    }
    setOpen(true);
  };

  return (
    <>
      {!!shouldShowOnboarding && onboardingComponent}
      <OnboardingHighlightWrapper
        wrap={!!onboardingComponent}
        highlightName="ManagerProfileGallery"
      >
        <Root ref={scrollRef}>
          <Content center>
            <Header>
              <Horizontal>
                <FontAwesomeIcon icon={faCardsBlank} />
                <BodyM bold>
                  <Ellipsis>
                    <FormattedMessage
                      {...(isMeUser ? navLabels.myCards : messages.cards)}
                    />
                  </Ellipsis>
                </BodyM>
              </Horizontal>
              <Horizontal>
                {!!cardCounts && (
                  <>
                    {sport === Sport.NBA && isType(cardCounts, 'CardCount') && (
                      <ScarcityCount
                        galleryUrl={galleryUrl}
                        slug={slug}
                        scarcity={Rarity.common}
                        count={cardCounts[Rarity.common]}
                      />
                    )}
                    {playableBlockchainRarities.map(scarcity => {
                      const scarcityKey = toCamelCase(scarcity);
                      if (cardCounts[scarcityKey] === 0) {
                        return null;
                      }
                      return (
                        <ScarcityCount
                          key={scarcity}
                          galleryUrl={galleryUrl}
                          slug={slug}
                          scarcity={scarcity}
                          count={cardCounts[scarcityKey]}
                        />
                      );
                    })}
                    {sport === Sport.FOOTBALL &&
                      isType(cardCounts, 'CardCount') &&
                      !!cardCounts.customSeries && (
                        <ScarcityCount
                          galleryUrl={galleryUrl}
                          slug={slug}
                          scarcity={Rarity.custom_series}
                          count={cardCounts.customSeries}
                        />
                      )}
                  </>
                )}
              </Horizontal>
            </Header>
            <CardsWrapper center gap={5}>
              <HighlightedDeckTitle gap={2}>
                <>
                  {!!isMeUser && (
                    <HeadlineL as="h3" className="text-center">
                      {highlightedDeck?.name || (
                        <FormattedMessage {...navLabels.hallOfFame} />
                      )}
                    </HeadlineL>
                  )}
                  {!!isMeUser && (
                    <>
                      <ButtonBase
                        disabled={loading}
                        onClick={onEditDeckClick}
                        aria-label={formatMessage({
                          id: 'ManagerProfile.HallOfFame.pickOtherCards',
                          defaultMessage: 'Edit highlight',
                        })}
                      >
                        <FontAwesomeIcon icon={faPen} size="xs" />
                      </ButtonBase>
                      {!!hasHighlightedDeck && (
                        <ButtonBaseWithConfirmDialog
                          onConfirm={() => {
                            updateSportProfile({
                              sport,
                              highlightedDeckId: '',
                            });
                          }}
                          message={
                            <BodyM className="text-center">
                              <FormattedMessage
                                id="DeckEditorDialog.areYouSureStopHighlight"
                                defaultMessage="Are you sure you want to stop highlighting <b>{list}</b>?"
                                values={{
                                  list: highlightedDeck.name,
                                  b: Bold,
                                }}
                              />
                            </BodyM>
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </ButtonBaseWithConfirmDialog>
                      )}
                    </>
                  )}
                </>
              </HighlightedDeckTitle>
              <GalleryCards
                cards={cardsToDisplay}
                hasHighlightedDeck={hasHighlightedDeck}
                maxCardsToDisplay={MAX_CARDS_TO_DISPLAY}
              />
            </CardsWrapper>
            <Footer>
              {isMeUser ? (
                <Button
                  size="medium"
                  disabled={loading}
                  fullWidth
                  to={generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)}
                  color="quaternary"
                >
                  <FormattedMessage {...messages.seeCollections} />
                </Button>
              ) : (
                <Button
                  size="small"
                  color="quaternary"
                  fullWidth
                  to={generateSportContextPath(
                    ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
                    {
                      params: {
                        slug,
                      },
                    }
                  )}
                >
                  <FormattedMessage {...messages.seeCollections} />
                </Button>
              )}
              <ProfileCta to={galleryUrl}>
                <FormattedMessage
                  {...(isMeUser ? messages.seeMyCards : messages.seeCards)}
                />
              </ProfileCta>
            </Footer>
          </Content>
          {extra}
        </Root>
      </OnboardingHighlightWrapper>
      {!!open && (
        <ChooseDeck
          highlightedDeckId={highlightedDeck?.id}
          sport={sport}
          open
          onClose={() => setOpen(false)}
          onSubmit={deckId => {
            updateSportProfile({
              sport,
              highlightedDeckId: idFromObject(deckId),
            });
          }}
          onAddDeck={onAddDeck}
          onEditDeck={onEditDeck}
        />
      )}
    </>
  );
};

Gallery.fragments = {
  deck: gql`
    fragment Gallery_deck on Deck {
      id
      slug
      name
      cards(first: 5) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
    }
    ${cardFragment}
  ` as TypedDocumentNode<Gallery_deck>,
  publicUserInfoInterface: gql`
    fragment Gallery_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      cardCounts(sport: $sport) {
        common
        limited
        rare
        superRare
        unique
        customSeries
      }
    }
  ` as TypedDocumentNode<Gallery_publicUserInfoInterface>,
};
